import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import SitePage, { CallToAction, calendly, styles, T, ACCENT } from '../components/site/SitePage';

const PATH = '/reappro-prevision-stock';

const SIGNS = [
  ['Rupture sur le best-seller, six mois de stock sur le reste.', "La moyenne d'un fichier ne voit ni la saison ni les produits qui s'essoufflent."],
  ['Les exports Amazon et Shopify recopiés chaque lundi.', 'Une demi-journée par semaine, et une erreur de copier-coller suffit.'],
  ['Des frais de stockage Amazon qui grimpent.', 'Sans savoir quels produits dorment, ni depuis quand.'],
  ['Les commandes de Noël décidées au feeling, en juillet.', 'Trop, et la trésorerie dort en entrepôt. Pas assez, et décembre se vend chez les autres.']
];

const FEATURES = [
  ['Une prévision par produit et par canal', "Ventes du site, d'Amazon, des boutiques et des revendeurs additionnées, avec la saison et vos temps forts."],
  ['Un point de commande par référence', 'Le calcul du stock de sécurité, sur tout le catalogue, avec le vrai délai de chaque fournisseur.'],
  ['Les contraintes fournisseur', 'Minimum de commande, colisage, conteneur à remplir : la quantité proposée est commandable.'],
  ['Le stock là où il est', "Entrepôt, Amazon et marchandise en route dans une seule vue, et les envois vers Amazon à préparer."]
];

const CAS = [
  ['Vous fabriquez vous-même', "Atelier, usine, labo ou façonnier : l'outil propose les séries à lancer selon votre capacité, et les matières et composants à commander à temps."],
  ['Vous vendez par taille et par coloris', 'La commande de saison taille par taille, à partir des ventes passées et des précommandes de vos revendeurs, puis le réassort pendant la saison.'],
  ['Vos ventes passent par des boutiques et des revendeurs', "Le stock de chaque magasin, de l'entrepôt et du site au même endroit, et le réassort de chacun selon ce qu'il vend."],
  ['Vos produits ont une date de péremption', "Les lots et les dates sont suivis : l'outil fait sortir en premier ce qui périme en premier, et vous prévient avant qu'un lot ne soit perdu."]
];

const FAQ = [
  ['Quelle différence avec un logiciel de gestion de stock ?', "Un logiciel du marché vous impose sa façon de travailler, et se paie par utilisateur. Ici, l'outil suit vos règles : vos fournisseurs, vos canaux, vos délais. Le code et les données vous appartiennent."],
  ['Mes données sont éparpillées dans plusieurs Excel, c’est un problème ?', "C'est le point de départ habituel. Je reprends vos fichiers (fournisseurs, ventes aux revendeurs), je connecte Amazon et Shopify directement à une seule base, puis je construis l'outil dessus. Rien à installer, aucun logiciel à changer."],
  ['Shopify a arrêté Stocky : par quoi le remplacer ?', "Si vous vendez aussi sur Amazon, c'est l'occasion de regrouper la réappro des deux canaux dans un seul outil, plutôt que de reprendre une application qui ne voit que Shopify."],
  ['Combien de temps avant un outil utilisable ?', 'Trois semaines pour un premier outil en ligne, sur vos vraies données, validé par votre équipe.']
];

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

// Aperçu de l'écran de réappro, données fictives. La gourde reprend l'exemple du calculateur (délai 75 j :
// le point de commande, 1 314, couvre 87 jours de ventes, d'où « Dans 9 j » avec 96 jours de couverture).
const ROWS = [
  ['Gourde isotherme 750 ml', '15,1', '96 j', '75 j', 'Dans 9 j', '454', '#B45309'],
  ['Tapis de yoga en liège', '8,4', '41 j', '45 j', 'Commander', '620', '#C2410C'],
  ['Bouteille inox 1 L', '11,2', '63 j', '50 j', 'Dans 2 j', '380', '#B45309'],
  ['Lunch box bambou', '2,1', '214 j', '30 j', 'Surstock', '—', '#5A6068'],
  ['Thermos 500 ml', '6,7', '88 j', '35 j', 'OK', '—', '#0F766E']
];

const Preview = () => (
  <figure style={{ margin: 0, background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 24px 48px -32px rgba(21,23,26,.45)' }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${T.line}`, background: '#F7F8FA' }}>
      <span style={{ fontWeight: 700, fontSize: 14 }}>Réappro · semaine 41</span>
      <span style={{ fontFamily: T.mono, fontSize: 12, color: T.faint }}>Données de démonstration</span>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ fontFamily: T.mono, fontSize: 12, color: T.faint, textAlign: 'left' }}>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>Produit</th>
            <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>Ventes / jour</th>
            <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>Couverture</th>
            <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>Délai d'appro</th>
            <th style={{ padding: '10px 14px', fontWeight: 500 }}>Statut</th>
            <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>À commander</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([name, sales, cover, lead, status, qty, color]) => (
            <tr key={name} style={{ borderTop: `1px solid ${T.line}` }}>
              <td style={{ padding: '10px 14px', fontWeight: 600 }}>{name}</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: T.mono }}>{sales}</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: T.mono }}>{cover}</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: T.mono, color: T.muted }}>{lead}</td>
              <td style={{ padding: '10px 14px' }}><span style={{ display: 'inline-block', padding: '2px 8px', border: `1px solid ${color}`, color, borderRadius: 3, fontFamily: T.mono, fontSize: 12, whiteSpace: 'nowrap' }}>{status}</span></td>
              <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: T.mono, fontWeight: 600 }}>{qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </figure>
);

const ReapproPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SitePage>
      <SEO
        title="Réappro et prévision de stock Amazon + Shopify"
        description="Un outil de réappro sur mesure pour les marques Amazon et Shopify : prévision par produit, point de commande, contraintes fournisseur. Prix fixe."
        path={PATH}
        breadcrumb={[['Réappro et prévision de stock', PATH]]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      <section style={{ ...styles.container, padding: '56px 24px 0', display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center' }}>
        <div style={{ flex: '1 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <p style={styles.kicker}>Stock et achats</p>
          <h1 style={styles.h1}>Gestion de stock e‑commerce : savoir quoi commander, combien et quand</h1>
          <p style={styles.lead}>Un outil de réappro construit sur toutes vos ventes (site, Amazon, boutiques, revendeurs), vos délais de fournisseur ou de fabrication, et votre saison. Le lundi matin, la liste des commandes à passer est prête.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <a href={calendly('reappro')} target="_blank" rel="noopener" className="lift" style={styles.button}>Réserver un appel de 30 min</a>
            <Link to="/outils/calcul-stock-de-securite" className="lift" style={{ ...styles.button, background: '#FFFFFF', color: T.ink, border: `1px solid ${T.ink}`, fontWeight: 600 }}>Essayer le calculateur</Link>
          </div>
        </div>
        <div style={{ flex: '1 1 520px', minWidth: 0 }}>
          <Preview />
        </div>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Les signes que votre fichier de réappro ne suit plus</h2>
        <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: 1, background: T.line, border: `1px solid ${T.line}` }}>
          {SIGNS.map(([title, text]) => (
            <div key={title} style={{ padding: 22, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <p style={{ margin: 0, fontWeight: 700 }}>{title}</p>
              <p style={{ margin: 0, fontSize: 15, color: T.muted }}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Ce que fait l'outil</h2>
        <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 28 }}>
          {FEATURES.map(([title, text], i) => (
            <div key={title} style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: `2px solid ${ACCENT}`, paddingTop: 14 }}>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: ACCENT }}>0{i + 1}</span>
              <p style={{ margin: 0, fontSize: 19, fontWeight: 800, fontStretch: '88%', lineHeight: 1.2 }}>{title}</p>
              <p style={{ margin: 0, fontSize: 16, color: T.text }}>{text}</p>
            </div>
          ))}
        </div>
        <p style={{ margin: '28px 0 0', fontSize: 17, color: T.text, maxWidth: 760 }}>Le calcul de base est en accès libre : <Link to="/outils/calcul-stock-de-securite">calculez le stock de sécurité et le point de commande</Link> d'un produit. L'outil fait la même chose sur tout le catalogue, chaque semaine. <a href="/#equipes">Voir la démo</a>. Vous utilisiez Stocky sur Shopify ? Voici <Link to="/alternative-stocky">les alternatives à Stocky</Link>.</p>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Pas seulement Amazon et Shopify</h2>
        <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: 1, background: T.line, border: `1px solid ${T.line}` }}>
          {CAS.map(([title, text]) => (
            <div key={title} style={{ padding: 22, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <p style={{ margin: 0, fontWeight: 700 }}>{title}</p>
              <p style={{ margin: 0, fontSize: 15, color: T.muted }}>{text}</p>
            </div>
          ))}
        </div>
        <p style={{ margin: '28px 0 0', fontSize: 17, color: T.text, maxWidth: 760 }}>Je me branche sur ce que vous avez déjà : Shopify, PrestaShop, WooCommerce, Amazon, votre ERP (Odoo, Sage, Cegid…), votre caisse et vos Excel.</p>
      </section>

      <section style={{ ...styles.container, paddingTop: 88, display: 'flex', flexWrap: 'wrap', gap: 32 }}>
        <div style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2 style={styles.h2}>Un premier outil en 3 semaines, à prix fixe</h2>
          <p style={{ margin: 0, fontSize: 18, color: T.text }}>On part de vos Excel tels qu'ils sont, et Amazon et Shopify se connectent directement : plus d'export à recopier. Je range tout dans une seule base, puis je construis l'outil dessus, testé par votre équipe sur vos vraies données. Le code vous appartient.</p>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 0, background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint }}>Outil de réappro sur mesure</span>
          <span style={{ fontSize: 40, fontWeight: 800, fontStretch: '80%' }}><span style={{ fontSize: 16, color: T.muted, fontWeight: 600 }}>dès</span> 2 900 € <span style={{ fontSize: 16, color: T.muted, fontWeight: 600 }}>HT</span></span>
          <span style={{ fontSize: 15, color: T.text }}>En 3 semaines, un premier outil centré sur ce qui vous coûte le plus : la prévision et le point de commande, par exemple. Le reste s'ajoute ensuite sur la même base. Tarif de lancement : 2 030 € HT au lieu de 2 900 € pour un premier outil, si vous acceptez qu'il fasse l'objet d'une étude de cas.</span>
          <a href="/#offres" style={{ fontSize: 15 }}>Voir toutes les offres</a>
        </div>
      </section>

      <section style={{ ...styles.narrow, paddingTop: 88 }}>
        <h2 style={styles.h2}>Questions fréquentes</h2>
        <div style={{ marginTop: 20, borderTop: `1px solid ${T.line2}` }}>
          {FAQ.map(([q, a]) => (
            <details key={q} style={{ borderBottom: `1px solid ${T.line2}`, padding: '16px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>{q}</summary>
              <p style={{ margin: '10px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <CallToAction from="reappro-bas" title="Parlons de votre réappro" text="30 minutes pour voir ce que l'outil changerait chez vous, puis un prix fixe par écrit sous 3 jours." />
    </SitePage>
  );
};

export default ReapproPage;
